/* eslint-disable react/no-array-index-key */
import React from "react";
import _ from "lodash";

// CONSTANTS
export const CHART_THEME = {
  labels: {
    text: {
      fontSize: 14
    }
  },
  axis: {
    legend: {
      text: {
        fontSize: 16,
        fill: "#5e5e5e"
      }
    },
    ticks: {
      text: {
        fontSize: 14,
        fill: "#5e5e5e"
      }
    }
  },
  legends: {
    text: {
      fontSize: 14,
      fill: "#5e5e5e"
    }
  }
};

export const LEGEND_CONFIG = {
  dataFrom: "keys",
  anchor: "bottom-right",
  direction: "column",
  justify: false,
  translateX: 120,
  translateY: 0,
  itemsSpacing: 3,
  itemWidth: 100,
  itemHeight: 20,
  itemDirection: "left-to-right",
  symbolSize: 20,
  symbolBorderWidth: 1.5
};

export const renderTruncatedText = (d, maxLength = 35) => {
  const { opacity, textAnchor, textBaseline, value = "", x, y } = d;

  // Truncate text and add ellipsis if longer than maxLength
  const truncatedValue =
    value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;

  return (
    <g transform={`translate(${x},${y})`} style={{ opacity }}>
      <line
        x1="0"
        x2="-5"
        y1="0"
        y2="0"
        style={{
          stroke: "#777777",
          strokeWidth: 1
        }}
      />
      <text
        alignmentBaseline={textBaseline}
        textAnchor={textAnchor}
        transform="translate(-10, 0)"
        style={{
          fontFamily: "sans-serif",
          fontSize: 14,
          fill: "#5e5e5e"
        }}
      >
        <tspan x="-10" dy="0.35em">
          {truncatedValue}
        </tspan>
      </text>
    </g>
  );
};

// FOCUSABLE BAR LAYER
const FOCUS_STYLES = {
  focusStroke: "#ffffff",
  focusStrokeWidth: "3",
  focusFilter: "drop-shadow(0 0 2px #0078d4) drop-shadow(0 0 2px #0078d4)",
  blurStroke: "transparent",
  blurStrokeWidth: "0",
  blurFilter: "none"
};

export const createFocusableBarLayer = (
  statusKeys,
  handleChartClick,
  clickType,
  indexKey,
  sortConfig = {
    sortBy: ["data.indexValue"],
    order: ["desc"],
    statusOrder: "asc"
  }
) => {
  let isMouseFocus = false;

  return ({ bars }) => {
    const fullSortBy = [
      ...sortConfig.sortBy,
      bar => statusKeys.indexOf(bar.data.id)
    ];
    const fullOrder = [...sortConfig.order, sortConfig.statusOrder || "asc"];

    const sortedBars = _.orderBy(bars, fullSortBy, fullOrder);

    return sortedBars.map(bar => (
      <rect
        key={`focus-${bar.data.indexValue}-${bar.data.id}`}
        x={bar.x}
        y={bar.y}
        width={bar.width}
        height={bar.height}
        fill="transparent"
        stroke="transparent"
        strokeWidth="0"
        tabIndex="0"
        role="button"
        aria-label={`${bar.data.id}: ${
          bar.data.value || 0
        } recommendations in ${bar.data.indexValue}. Press Enter to filter.`}
        style={{
          cursor: "pointer",
          outline: "none",
          pointerEvents: "all"
        }}
        onMouseDown={() => {
          isMouseFocus = true;
          setTimeout(() => {
            isMouseFocus = false;
          }, 100);
        }}
        onFocus={e => {
          if (!isMouseFocus) {
            e.target.style.stroke = FOCUS_STYLES.focusStroke;
            e.target.style.strokeWidth = FOCUS_STYLES.focusStrokeWidth;
            e.target.style.filter = FOCUS_STYLES.focusFilter;
          }
        }}
        onBlur={e => {
          e.target.style.stroke = FOCUS_STYLES.blurStroke;
          e.target.style.strokeWidth = FOCUS_STYLES.blurStrokeWidth;
          e.target.style.filter = FOCUS_STYLES.blurFilter;
        }}
        onClick={() => {
          handleChartClick(
            {
              id: bar.data.id,
              indexValue: bar.data.indexValue,
              value: bar.data.value
            },
            clickType,
            indexKey
          );
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleChartClick(
              {
                id: bar.data.id,
                indexValue: bar.data.indexValue,
                value: bar.data.value
              },
              clickType,
              indexKey
            );
          }
        }}
      />
    ));
  };
};

// CONTAINER STYLES
export const getChartContainerProps = () => {
  let isMouseFocus = false;

  return {
    className: "reco-detailed-stats-feature-chart",
    tabIndex: "0",
    role: "img",
    style: { outline: "none" },
    onMouseDown: () => {
      isMouseFocus = true;
      setTimeout(() => {
        isMouseFocus = false;
      }, 100);
    },
    onFocus: e => {
      if (!isMouseFocus) {
        e.target.style.outline = "2px solid #0078d4";
      }
    },
    onBlur: e => {
      e.target.style.outline = "none";
    }
  };
};

// Graph Text Color functions

const getRelativeLuminance = hexColor => {
  // Remove # if present
  const hex = hexColor.replace("#", "");

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  // Apply gamma correction
  const rsRGB = r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4;
  const gsRGB = g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4;
  const bsRGB = b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4;

  return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
};

const getContrastRatio = (color1, color2) => {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

// WCAG 2.2 AA requires a contrast ratio of at least 3:1 for graphical objects and UI components
const MIN_CONTRAST_RATIO = 3;
const BACKGROUND_COLOR = "#ffffff";

export default function checkColorContrast2(
  hexColor,
  backgroundColor = BACKGROUND_COLOR
) {
  if (!hexColor) return { passes: true, ratio: null };

  try {
    const ratio = getContrastRatio(hexColor, backgroundColor);
    const fails = ratio < MIN_CONTRAST_RATIO;

    return { fails, ratio: ratio.toFixed(2) };
  } catch (e) {
    console.error("Error checking contrast:", e);
    return { fails: false, ratio: null };
  }
}

export const getAccessibleTextColor = backgroundColor => {
  const whiteContrast = getContrastRatio(backgroundColor, "#FFFFFF");
  const blackContrast = getContrastRatio(backgroundColor, "#000000");

  // Return color with better contrast (need 4.5:1 for normal text)
  return whiteContrast > blackContrast ? "#FFFFFF" : "#000000";
};
